import type { Statement } from '../parser/ast.js';
import { replaceChain, replaceNode } from './detect.js';
import {
  findForLoopsFromRepeatUntil,
  findForLoopsFromWhile,
  forToGuardedRepeatUntil,
  forToRepeatUntil,
  forToWhile,
  matchGuardedRepeatUntilAsWhile,
  matchRepeatUntilAsWhile,
  repeatUntilToWhile,
  whileToGuardedRepeatUntil,
  whileToRepeatUntil,
} from './repetition.js';

/**
 * Αυτόματος εντοπισμός δομών επανάληψης (ΓΙΑ/ΟΣΟ/ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ) με
 * τουλάχιστον μία έγκυρη μετατροπή — ίδια λογική με το `detect.ts` για τις
 * δομές επιλογής, αλλά ξεχωριστό αρχείο/συνάρτηση αφού πρόκειται για
 * διαφορετικό είδος δομών. Κάθε `apply` είναι ήδη "root-aware" (δέχεται όλο
 * το σώμα και επιστρέφει το νέο σώμα), οπότε δεν χρειάζεται ξεχωριστό βήμα
 * αντικατάστασης από τον καλούντα.
 */

export type RepetitionShape = 'for' | 'while' | 'repeat-until';

export interface RepetitionOption {
  readonly targetShape: RepetitionShape;
  readonly apply: (root: readonly Statement[]) => readonly Statement[];
}

export interface RepetitionDetection {
  // Πάντα 'node' πια — οι πρώην ξεχωριστές "pair" μετατροπές (ΟΣΟ/ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ
  // → ΓΙΑ, που καταναλώνουν ΚΑΙ την εντολή αρχικοποίησης πριν τον βρόχο)
  // ενώνονται πλέον ΜΕΣΑ στο `options` του ΙΔΙΟΥ detection με τον βρόχο, όχι
  // σε ξεχωριστό αντικείμενο (βλ. σχόλιο `visitList` — bug: το UI αντιμετώπιζε
  // "→ ΓΙΑ" και "→ ΜΕΧΡΙΣ_ΟΤΟΥ"/"→ ΟΣΟ" σαν άσχετα μεταξύ τους, το ένα δεν
  // αποεπέλεγε το άλλο). Το πεδίο μένει (αντί να αφαιρεθεί) για συμβατότητα
  // σχήματος με το `SelectionDetection.kind` — απλά έχει πια ΜΙΑ τιμή.
  readonly kind: 'node';
  /** Βάθος εμφώλευσης δομών επανάληψης (1 = εξωτερικότερη, 2 = αμέσως επόμενη προς τα μέσα, κ.ο.κ.) — βλ. `detectRepetitionStructures`. */
  readonly level: number;
  /** Ο κόμβος-αγκύρωση της δομής (η ίδια η δομή επανάληψης) — χρησιμοποιείται στο main.ts μαζί με `computeNodeDepths` για να καθοριστεί ασφαλής σειρά όταν εφαρμόζονται πολλαπλές, ταυτόχρονα επιλεγμένες μετατροπές. */
  readonly anchor: Statement;
  readonly options: readonly RepetitionOption[];
}

/**
 * Το `level` μετράει ΜΟΝΟ πραγματική εμφώλευση δομών επανάληψης (σώμα ΓΙΑ/
 * ΟΣΟ/ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ) — η είσοδος σε κλάδο ΑΝ/ΕΠΙΛΕΞΕ (π.χ. για να βρεθεί
 * ένα τυλιγμένο ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ) ΔΕΝ αυξάνει το βάθος, αφού δεν αντιστοιχεί
 * σε νέο επίπεδο επανάληψης.
 */
export function detectRepetitionStructures(statements: readonly Statement[]): RepetitionDetection[] {
  const items: RepetitionDetection[] = [];

  function visitList(list: readonly Statement[], level: number, claimed?: Statement): void {
    // Οι "pair" μετατροπές (ΟΣΟ/ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ → ΓΙΑ) καταναλώνουν ΚΑΙ την
    // εντολή αρχικοποίησης ΠΡΙΝ τον βρόχο — ΔΕΝ τις προσθέτουμε ως δικό τους
    // ξεχωριστό detection εδώ πια. Τις κρατάμε σε Map, βάσει του ΙΔΙΟΥ του
    // βρόχου (anchor), ώστε να ΕΝΩΘΟΥΝ πιο κάτω με τις υπόλοιπες επιλογές
    // ΤΟΥ ΙΔΙΟΥ βρόχου σε ΕΝΑ detection — αλλιώς (bug report χρήστη): "→ ΓΙΑ"
    // και "→ ΜΕΧΡΙΣ_ΟΤΟΥ"/"→ ΟΣΟ" ήταν 2 ΤΕΛΕΙΩΣ ξεχωριστά αντικείμενα για
    // ΤΟΝ ΙΔΙΟ βρόχο, οπότε το ένα δεν "έσβηνε" το άλλο όταν άλλαζε επιλογή.
    const forFromWhile = new Map<Statement, RepetitionOption>();
    for (const match of findForLoopsFromWhile(list)) {
      const pair = [match.initAssignment, match.whileStatement] as const;
      forFromWhile.set(match.whileStatement, { targetShape: 'for', apply: (root) => replaceChain(root, pair, match.apply()) });
    }
    const forFromRepeatUntil = new Map<Statement, RepetitionOption>();
    for (const match of findForLoopsFromRepeatUntil(list)) {
      const pair = [match.initAssignment, match.repeatStatement] as const;
      forFromRepeatUntil.set(match.repeatStatement, { targetShape: 'for', apply: (root) => replaceChain(root, pair, match.apply()) });
    }

    for (let i = 0; i < list.length; i++) {
      const stmt = list[i]!;
      // `claimed` = το ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ που ήδη καλύπτεται από ένα αναγνωρισμένο
      // "Αν+φρουρός" ζεύγος στο ΕΞΩΤΕΡΙΚΟ επίπεδο (βλ. `case 'IfStatement'`
      // παρακάτω) — ΔΕΝ του δίνουμε ΚΑΙ δικό του, κατώτερο detection (θα ήταν
      // περιττό δεύτερο κουμπί "→ ΟΣΟ", με την κατώτερη "διπλασιασμένου
      // σώματος" μορφή, δίπλα στο ήδη καθαρό της εξωτερικής αναγνώρισης).
      if (stmt.kind === 'RepeatUntilStatement' && stmt !== claimed) {
        const options: RepetitionOption[] = [];
        const forOption = forFromRepeatUntil.get(stmt);
        if (forOption !== undefined) options.push(forOption);
        // «Γυμνό» ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ → ΟΣΟ: η ΚΑΘΑΡΗ μορφή (χωρίς διπλασιασμό
        // σώματος) όταν αποδεικνύεται ασφαλής από τις εντολές ΠΡΙΝ το (βλ.
        // σχόλιο `matchRepeatUntilAsWhile)· αλλιώς το `repeatUntilToWhile`
        // (ΠΑΝΤΑ έγκυρο, με διπλασιασμό σώματος) — ποτέ "καμία μετατροπή".
        const asWhile = matchRepeatUntilAsWhile(list.slice(0, i), stmt);
        if (asWhile !== null) {
          options.push({ targetShape: 'while', apply: (root) => replaceNode(root, stmt, [asWhile]) });
        } else {
          const duplicated = repeatUntilToWhile(stmt);
          options.push({ targetShape: 'while', apply: (root) => replaceNode(root, stmt, duplicated) });
        }
        if (options.length > 0) items.push({ kind: 'node', level, anchor: stmt, options });
      }
      visit(stmt, level, forFromWhile, list.slice(0, i));
    }
  }

  function visit(stmt: Statement, level: number, forFromWhile: ReadonlyMap<Statement, RepetitionOption>, before: readonly Statement[]): void {
    switch (stmt.kind) {
      case 'ForStatement': {
        const options: RepetitionOption[] = [];
        const asWhile = forToWhile(stmt);
        if (asWhile !== null) options.push({ targetShape: 'while', apply: (root) => replaceNode(root, stmt, asWhile) });
        const asRepeatUntil = forToRepeatUntil(stmt);
        if (asRepeatUntil !== null) {
          options.push({ targetShape: 'repeat-until', apply: (root) => replaceNode(root, stmt, asRepeatUntil) });
        } else {
          // Τα όρια δεν αποδεικνύονται σταθερές ≥1 φορά (π.χ. διαβασμένη τιμή) —
          // προσφέρουμε την "guarded" μορφή αντί να αρνηθούμε εντελώς, ίδια
          // λογική με το WhileStatement πιο κάτω.
          const guardedRepeatUntil = forToGuardedRepeatUntil(stmt);
          if (guardedRepeatUntil !== null) {
            options.push({ targetShape: 'repeat-until', apply: (root) => replaceNode(root, stmt, guardedRepeatUntil) });
          }
        }
        if (options.length > 0) items.push({ kind: 'node', level, anchor: stmt, options });
        visitList(stmt.body, level + 1);
        return;
      }
      case 'WhileStatement': {
        const options: RepetitionOption[] = [];
        const forOption = forFromWhile.get(stmt);
        if (forOption !== undefined) options.push(forOption);
        // Η συνθήκη αποδεικνύεται ΗΔΗ αληθής πριν την πρώτη επανάληψη (βλ.
        // `whileToRepeatUntil`) → ο φρουρός είναι περιττός, προσφέρουμε την
        // απλή μορφή· αλλιώς πέφτουμε πίσω στην πάντα-ασφαλή φρουρούμενη.
        const direct = whileToRepeatUntil(before, stmt);
        if (direct !== null) {
          options.push({ targetShape: 'repeat-until', apply: (root) => replaceNode(root, stmt, [direct]) });
        } else {
          const guarded = whileToGuardedRepeatUntil(stmt);
          options.push({ targetShape: 'repeat-until', apply: (root) => replaceNode(root, stmt, [guarded]) });
        }
        items.push({ kind: 'node', level, anchor: stmt, options });
        visitList(stmt.body, level + 1);
        return;
      }
      case 'RepeatUntilStatement':
        visitList(stmt.body, level + 1);
        return;
      case 'IfStatement': {
        const asWhile = matchGuardedRepeatUntilAsWhile(stmt);
        if (asWhile !== null) {
          items.push({ kind: 'node', level, anchor: stmt, options: [{ targetShape: 'while', apply: (root) => replaceNode(root, stmt, [asWhile]) }] });
        }
        // `thenBranch[0]` είναι ΑΚΡΙΒΩΣ το ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ που μόλις
        // αναγνωρίστηκε (βλ. `matchGuardedRepeatUntilAsWhile`) — του το
        // σημειώνουμε ως "claimed" ώστε το `visitList` να ΜΗΝ του δώσει ΚΑΙ
        // δικό του, κατώτερο (διπλασιασμένου σώματος) detection.
        visitList(stmt.thenBranch, level, asWhile !== null ? stmt.thenBranch[0] : undefined);
        for (const branch of stmt.elseIfBranches) visitList(branch.body, level);
        if (stmt.elseBranch !== null) visitList(stmt.elseBranch, level);
        return;
      }
      case 'SwitchStatement':
        for (const c of stmt.cases) visitList(c.body, level);
        if (stmt.elseBody !== null) visitList(stmt.elseBody, level);
        return;
      default:
        return;
    }
  }

  visitList(statements, 1);
  return items;
}
